import React, { useState, useEffect } from 'react';
import { Shield, Cookie, Check, X, Sliders, Lock, ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface CookiePreferences {
  necessary: boolean; // Технические (всегда true)
  analytics: boolean; // Яндекс Метрика, client-id
  marketing: boolean; // UTM-метки, телеметрия
  timestamp: string;
}

const STORAGE_KEY = 'sp_cookie_consent_v2_2026';

export const CookieConsentManager: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: true,
    marketing: true,
    timestamp: ''
  });

  useEffect(() => {
    try {
      // Поддержка сброса через URL параметр ?reset_cookies=1
      if (typeof window !== 'undefined' && window.location.search.includes('reset_cookies')) {
        localStorage.removeItem(STORAGE_KEY);
      }

      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // Показываем плашку для посетителя через 300мс
        const timer = setTimeout(() => setIsVisible(true), 300);
        return () => clearTimeout(timer);
      } else {
        const parsed: CookiePreferences = JSON.parse(saved);
        setPreferences(parsed);
      }
    } catch {
      // Fallback
      setIsVisible(true);
    }

    // Слушатель для открытия настроек из футера
    const handleOpenSettings = () => {
      setShowSettingsModal(true);
      setIsVisible(false);
    };

    const handleOpenBanner = () => {
      setIsVisible(true);
    };

    window.addEventListener('sp_open_cookie_settings', handleOpenSettings);
    window.addEventListener('sp_open_cookie_banner', handleOpenBanner);
    return () => {
      window.removeEventListener('sp_open_cookie_settings', handleOpenSettings);
      window.removeEventListener('sp_open_cookie_banner', handleOpenBanner);
    };
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    const updated = { ...prefs, timestamp: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      document.cookie = `sp_cookie_consent=1; max-age=31536000; path=/; SameSite=Lax`;
    } catch {
      // safe fallback
    }
    setPreferences(updated);
    setIsVisible(false);
    setShowSettingsModal(false);

    // Уведомление систем аналитики
    window.dispatchEvent(new CustomEvent('sp_cookie_consent_updated', { detail: updated }));
  };

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: ''
    });
  };

  const handleAcceptNecessaryOnly = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: ''
    });
  };

  return (
    <>
      {/* 1. Институциональный баннер внизу экрана (поверх плавающего дока, z-[100]) */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-20 sm:bottom-6 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-xl z-[100] pointer-events-auto"
          >
            <div className="bg-white border border-[#EAE5DE] rounded-[4px] shadow-[0_20px_50px_rgba(20,20,22,0.18)] p-5 sm:p-6 text-[#141416] text-xs font-sans backdrop-blur-xl ring-1 ring-black/5">
              <div className="flex items-start justify-between gap-4 mb-3 pb-3 border-b border-[#EAE5DE]">
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-[2px] bg-[#8C6D37]/10 border border-[#8C6D37]/30 flex items-center justify-center text-[#8C6D37]">
                    <Cookie className="w-3.5 h-3.5" />
                  </div>
                  <span className="font-mono text-[11px] font-bold tracking-wider text-[#141416] uppercase">
                    КОМПЛАЕНС ОБРАБОТКИ ДАННЫХ // ФЗ № 152-ФЗ
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleAcceptNecessaryOnly}
                  className="text-[#141416]/40 hover:text-[#141416] transition-colors cursor-pointer p-1"
                  aria-label="Закрыть"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-[12px] text-[#141416]/75 leading-relaxed mb-4 font-normal">
                Мы используем файлы cookie и технические идентификаторы (включая «Яндекс Метрику») 
                для защищенного функционирования платформы в соответствии с{' '}
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('sp_open_legal_doc', { detail: 'master-terms' }))}
                  className="text-[#8C6D37] hover:text-[#785D2E] underline underline-offset-2 font-medium cursor-pointer"
                >
                  ФЗ № 152-ФЗ «О персональных данных»
                </button>
                . Данные передаются по зашифрованному протоколу TLS 1.3.
              </p>

              <div className="flex flex-wrap items-center gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="bg-[#8C6D37] hover:bg-[#785D2E] text-white font-mono text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-[2px] transition-colors cursor-pointer shadow-xs"
                >
                  Принять все
                </button>
                <button
                  type="button"
                  onClick={handleAcceptNecessaryOnly}
                  className="bg-[#FAF8F5] hover:bg-[#F2EFE9] text-[#141416] font-mono text-[11px] uppercase tracking-wider px-3.5 py-2 rounded-[2px] transition-colors cursor-pointer border border-[#EAE5DE]"
                >
                  Только технические
                </button>
                <button
                  type="button"
                  onClick={() => setShowSettingsModal(true)}
                  className="text-[#8C6D37] hover:text-[#785D2E] font-mono text-[11px] font-semibold underline underline-offset-4 px-2 py-2 transition-colors cursor-pointer ml-auto flex items-center gap-1.5"
                >
                  <Sliders className="w-3 h-3" />
                  <span>Настроить</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. Модальное окно детальной настройки категорий (152-ФЗ) */}
      <AnimatePresence>
        {showSettingsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-[#0B0B0C]/70 backdrop-blur-md">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-xl bg-[#FAF8F5] border border-[#EAE5DE] rounded-[4px] shadow-2xl p-6 sm:p-8 text-[#141416] font-sans overflow-hidden"
            >
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-[#EAE5DE]">
                <div className="flex items-center gap-2.5">
                  <Shield className="w-4 h-4 text-[#8C6D37]" />
                  <span className="font-mono text-xs font-bold uppercase tracking-wider text-[#141416]">
                    УПРАВЛЕНИЕ ФАЙЛАМИ COOKIE // 152-ФЗ
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSettingsModal(false)}
                  className="w-7 h-7 rounded-[2px] bg-[#EAE5DE]/60 text-[#141416] hover:bg-[#EAE5DE] flex items-center justify-center transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Категория 1: Технические */}
                <div className="p-4 bg-white border border-[#EAE5DE] rounded-[2px] flex items-start justify-between gap-4 shadow-2xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-[#141416]">Технические и защитные cookie</span>
                      <span className="font-mono text-[9px] text-emerald-800 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 rounded-[2px] font-semibold">
                        ОБЯЗАТЕЛЬНО
                      </span>
                    </div>
                    <p className="text-[12px] text-[#141416]/70 leading-relaxed">
                      Необходимы для работы шлюза TLS 1.3, сессий VDR-комнаты, защиты от CSRF и сохранения ваших правовых настроек. Не могут быть отключены.
                    </p>
                  </div>
                  <div className="shrink-0 pt-1">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      className="w-4 h-4 accent-[#8C6D37] cursor-not-allowed opacity-70"
                    />
                  </div>
                </div>

                {/* Категория 2: Аналитические */}
                <div className="p-4 bg-white border border-[#EAE5DE] rounded-[2px] flex items-start justify-between gap-4 shadow-2xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-[#141416]">Аналитические cookie</span>
                      <span className="font-mono text-[9px] text-[#8C6D37] bg-[#8C6D37]/10 border border-[#8C6D37]/30 px-1.5 py-0.5 rounded-[2px] font-semibold">
                        ЯНДЕКС МЕТРИКА
                      </span>
                    </div>
                    <p className="text-[12px] text-[#141416]/70 leading-relaxed">
                      Сбор деперсонифицированных технических метрик (client-id, тепловые карты скролла, время ответа калькулятора) для совершенствования интерфейса.
                    </p>
                  </div>
                  <div className="shrink-0 pt-1">
                    <input
                      type="checkbox"
                      id="cookie-analytics-check"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="w-4 h-4 accent-[#8C6D37] cursor-pointer"
                    />
                  </div>
                </div>

                {/* Категория 3: Маркетинговые и сессионные */}
                <div className="p-4 bg-white border border-[#EAE5DE] rounded-[2px] flex items-start justify-between gap-4 shadow-2xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-[#141416]">Маркетинговые и сессионные идентификаторы</span>
                    </div>
                    <p className="text-[12px] text-[#141416]/70 leading-relaxed">
                      Фиксация UTM-меток, источников перехода и рефереров для защиты от несанкционированного парсинга и контроля целевых обращений.
                    </p>
                  </div>
                  <div className="shrink-0 pt-1">
                    <input
                      type="checkbox"
                      id="cookie-marketing-check"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="w-4 h-4 accent-[#8C6D37] cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Информация о локализации (ч. 5 ст. 18 152-ФЗ) */}
              <div className="p-3 bg-white border border-[#EAE5DE] rounded-[2px] font-mono text-[11px] text-[#141416]/80 mb-6 flex items-center gap-2 shadow-2xs">
                <Lock className="w-3.5 h-3.5 text-[#8C6D37] shrink-0" />
                <span>Используются современные протоколы шифрования и облачные CDN.</span>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 pt-3 border-t border-[#EAE5DE]">
                <button
                  type="button"
                  onClick={handleAcceptNecessaryOnly}
                  className="px-4 py-2.5 bg-white hover:bg-[#FAF8F5] text-[#141416] font-mono text-xs uppercase tracking-wider rounded-[2px] transition-colors border border-[#EAE5DE] cursor-pointer font-medium"
                >
                  Отклонить опциональные
                </button>
                <button
                  type="button"
                  onClick={() => savePreferences(preferences)}
                  className="px-6 py-2.5 bg-[#8C6D37] hover:bg-[#785D2E] text-white font-mono text-xs font-bold uppercase tracking-wider rounded-[2px] transition-colors shadow-xs cursor-pointer"
                >
                  Сохранить выбор
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
